// client/src/pages/DashboardPage.jsx - Tableau de bord
import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import api from '../api/api';
import Header from '../components/Header';
import { Clock, Users, ChefHat, Star, PlayCircle } from 'lucide-react';
import Seo from '../components/Seo';

function DashboardPage() {
  const [recentRecipes, setRecentRecipes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('recipes'); // 'recipes' ou 'suggestions'
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Récupérer les recettes
        const recipesResponse = await api.post('/api/meals/recipes');
        if (recipesResponse.data.success) {
          const sortedRecipes = recipesResponse.data.recipes
            .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
            .reverse();
          setRecentRecipes(sortedRecipes);
        }

        // Récupérer les suggestions - adapter à la structure de réponse
        const suggestionsResponse = await api.get('/api/meals/suggestions');
        if (suggestionsResponse.data.success) {
          // Les suggestions sont dans suggestionsResponse.data.data
          setSuggestions(suggestionsResponse.data.data || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des données:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRecipeClick = (recipe) => {
    // Rediriger vers la page de recette ou l'assistant de cuisine selon le statut
    if (recipe.status === 'ready_to_cook' || recipe.status === 'cooking') {
      navigate(`/cooking-assistant?recipeIds=${recipe.id || recipe._id}`);
    } else {
      navigate(`/recette/${recipe.id || recipe._id}?lastPageType=dashboard`);
    }
  };

  const currentData = viewMode === 'recipes' ? recentRecipes : suggestions;

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen">
        <Categories />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Seo
        title="Tableau de bord"
        description="Retrouvez vos dernières recettes, suggestions personnalisées et vos listes de repas générées avec YAQAM."
        breadcrumb
      />
      <div className="bg-gray-50 min-h-screen pb-20 md:pb-8">
        <Categories />

        <div className="container mx-auto px-4 py-6">
        {/* En-tête */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Bonjour {user?.name} ! 👋
          </h1>
          <p className="text-gray-600">
            {viewMode === 'recipes' ? 'Voici vos dernières recettes' : 'Découvrez nos suggestions pour vous'}
          </p>
        </div>

        {/* Toggle entre recettes et suggestions - Toutes tailles d'écran */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-full p-1 shadow-md border border-gray-200">
            <button
              onClick={() => setViewMode('recipes')}
              className={`px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-medium transition-all ${
                viewMode === 'recipes'
                  ? 'text-white shadow-sm'
                  : 'hover:text-gray-800'
              }`}
              style={viewMode === 'recipes' 
                ? { backgroundColor: '#87A96B' } 
                : { color: '#6c757d' }
              }
            >
              <span className="hidden sm:inline">Mes Recettes ({recentRecipes.length})</span>
              <span className="sm:hidden">Recettes ({recentRecipes.length})</span>
            </button>
            <button
              onClick={() => setViewMode('suggestions')}
              className={`px-4 md:px-6 py-2 rounded-full text-xs md:text-sm font-medium transition-all ${
                viewMode === 'suggestions'
                  ? 'text-white shadow-sm'
                  : 'hover:text-gray-800'
              }`}
              style={viewMode === 'suggestions' 
                ? { backgroundColor: '#87A96B' } 
                : { color: '#6c757d' }
              }
            >
              <span className="hidden sm:inline">Suggestions ({suggestions.length})</span>
              <span className="sm:hidden">Suggestions ({suggestions.length})</span>
            </button>
          </div>
        </div>



        {/* Liste des recettes/suggestions */}
        {currentData.length === 0 ? (
          <div className="text-center py-16">
            <ChefHat className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              {viewMode === 'recipes' 
                ? 'Aucune recette encore' 
                : 'Aucune suggestion disponible'
              }
            </h3>
            <p className="text-gray-500 mb-6">
              {viewMode === 'recipes'
                ? 'Commencez par créer votre première recette !'
                : 'Les suggestions apparaîtront bientôt ici.'
              }
            </p>
            {viewMode === 'recipes' && (
              <Link
                to="/inspirations-repas"
                className="inline-flex items-center px-6 py-3 text-white rounded-full font-medium hover:opacity-90 transition-colors"
                style={{ backgroundColor: '#87A96B' }}
              >
                Créer une recette
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
            {currentData.map((recipe) => (
              <div
                key={recipe.id || recipe._id}
                onClick={() => handleRecipeClick(recipe)}
                className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all cursor-pointer border border-gray-100 overflow-hidden transform hover:scale-105"
              >
                {/* Image de la recette */}
                <div className="aspect-square w-full overflow-hidden bg-gray-100">
                  {recipe.imgUrlArray && recipe.imgUrlArray.length > 0 ? (
                    <img
                      src={recipe.imgUrlArray[0]}
                      alt={recipe.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ChefHat className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Badge suggestion */}
                {viewMode === 'suggestions' && (
                  <div className="absolute top-2 left-2">
                    <span className="text-white text-xs px-2 py-1 rounded-full font-medium"
                          style={{ backgroundColor: '#E67E22' }}>
                      Suggestion
                    </span>
                  </div>
                )}

                {/* Bouton rond "Générer la recette" pour les suggestions */}
                {viewMode === 'suggestions' && (
                  <div className="absolute top-2 right-2">
                    <button 
                      className="w-8 h-8 rounded-full text-white flex items-center justify-center hover:opacity-90 transition-all shadow-lg"
                      style={{ backgroundColor: '#87A96B' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRecipeClick(recipe);
                      }}
                    >
                      <PlayCircle className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Contenu de la carte */}
                <div className="p-3">
                  {/* En-tête avec titre et badge */}
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-gray-900 text-sm leading-tight flex-grow mr-1 line-clamp-2">
                      {recipe.title}
                    </h3>
                    {viewMode === 'recipes' && recipe.status && (
                      <span className={`px-1.5 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${
                        recipe.status === 'readyToCook' 
                          ? 'text-white'
                          : recipe.status === 'cooking'
                          ? 'text-white'
                          : recipe.status === 'finished'
                          ? 'text-white'
                          : 'text-white'
                      }`}
                      style={
                        recipe.status === 'readyToCook' 
                          ? { backgroundColor: '#87A96B' }
                          : recipe.status === 'cooking'
                          ? { backgroundColor: '#E67E22' }
                          : recipe.status === 'finished'
                          ? { backgroundColor: '#6c757d' }
                          : { backgroundColor: '#E67E22' }
                      }>
                        {recipe.status === 'readyToCook' 
                          ? 'Prêt à cuisiner' 
                          : recipe.status === 'cooking'
                          ? 'En cours'
                          : recipe.status === 'finished'
                          ? 'Terminé'
                          : 'Brouillon'
                        }
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-xs mb-3 line-clamp-2">
                    {recipe.description}
                  </p>
                  
                  {/* Infos rapides */}
                  <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                    {recipe.prepTime && (
                      <div className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        <span>{recipe.prepTime}min</span>
                      </div>
                    )}
                    {recipe.servings && (
                      <div className="flex items-center">
                        <Users className="w-3 h-3 mr-1" />
                        <span>{recipe.servings}</span>
                      </div>
                    )}
                    {recipe.dishType && (
                      <div className="flex items-center">
                        <span className="text-xs px-2 py-1 rounded" style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}>
                          {recipe.dishType}
                        </span>
                      </div>
                    )}
                    {recipe.rating && (
                      <div className="flex items-center">
                        <Star className="w-3 h-3 mr-1 fill-yellow-400 text-yellow-400" />
                        <span>{recipe.rating}</span>
                      </div>
                    )}
                  </div>

                  {/* Bouton d'action - seulement pour les recettes */}
                  {viewMode === 'recipes' && (
                    <>
                      {(recipe.status === 'ready_to_cook' || recipe.status === 'cooking') ? (
                        <button 
                          className="w-full flex items-center justify-center px-2 py-1.5 text-white text-xs rounded-md hover:opacity-90 transition-all font-medium"
                          style={{ backgroundColor: '#87A96B' }}
                        >
                          <PlayCircle className="w-3 h-3 mr-1" />
                          Cuisiner
                        </button>
                      ) : (
                        <button className="w-full flex items-center justify-center px-2 py-1.5 text-xs rounded-md hover:opacity-90 transition-colors font-medium"
                                style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}>
                          <ChefHat className="w-3 h-3 mr-1" />
                          Voir
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
    </>
  );
}

function Categories() {
  const location = useLocation();

  const categories = [
    { name: "Inspiration repas", icon: "🧠", url: "/inspirations-repas" },
    { name: "Commencer à cuisiner", icon: "👨‍🍳", url: "/cooking-assistant" },
    { name: "Noter les recettes", icon: "⭐", url: "/noter-recettes" },
  ];

  return (
    <div className="py-6 shadow-sm mb-6" style={{ background: 'linear-gradient(135deg, #87A96B 10%, #a8c285 50%, #87A96B 90%)' }}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
          {categories.map((category) => {
            const isActive = location.pathname === category.url;
            return (
              <Link to={category.url} key={category.name}>
                <div
                  className={`flex flex-col items-center p-6 rounded-2xl cursor-pointer transition-all transform hover:scale-105 hover:shadow-lg ${
                    isActive 
                      ? 'bg-white shadow-lg' 
                      : 'bg-white/80 backdrop-blur-sm shadow-md hover:bg-white hover:shadow-xl'
                  }`}
                  style={isActive ? { boxShadow: '0 0 0 2px #E67E22' } : {}}
                >
                  <div className={`text-3xl mb-3 p-3 rounded-full transition-colors ${
                    isActive 
                      ? 'text-white'
                      : 'bg-gray-100'
                  }`}
                  style={isActive ? { backgroundColor: '#E67E22' } : {}}>
                    {category.icon}
                  </div>
                  <span className={`text-sm font-semibold text-center leading-tight`}
                        style={{ color: isActive ? '#E67E22' : '#2C3E50' }}>
                    {category.name}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
